import { useMemo, useSyncExternalStore } from 'react';

const MINUTE_MS = 60_000;

/** One shared timer for every mounted clock, running only while one is. */
const listeners = new Set<() => void>();
let ticker: number | undefined;

function subscribeToClock(onTick: () => void): () => void {
  listeners.add(onTick);
  ticker ??= window.setInterval(() => {
    for (const listener of listeners) listener();
  }, MINUTE_MS / 4);
  return () => {
    listeners.delete(onTick);
    if (listeners.size > 0 || ticker === undefined) return;
    window.clearInterval(ticker);
    ticker = undefined;
  };
}

function subscribeToNothing(): () => void {
  return () => undefined;
}

/** Whole minutes since the epoch: the same number for every read within a minute. */
function currentMinute(): number {
  return Math.floor(Date.now() / MINUTE_MS);
}

/**
 * The current time for display, to the minute, re-rendering the caller when
 * the minute turns. A component must not read the clock while it renders:
 * React Compiler caches what render computes, so `new Date()` there keeps the
 * time of the first render. `fixed` pins the time instead (tests pass it).
 */
export function useNow(fixed?: Date): Date {
  const minute = useSyncExternalStore(fixed === undefined ? subscribeToClock : subscribeToNothing, currentMinute);
  const now = useMemo(() => new Date(minute * MINUTE_MS), [minute]);
  return fixed ?? now;
}
